import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {
  GET_ADMIN_AGENT_PERFORMANCE_STATISTICS,
  GET_ADMIN_AGENT_PERFORMANCE_TRANSACTION_HISTORY,
  GET_TEAM_AGENT_PERFORMANCE_STATISTICS,
  GET_TEAM_AGENT_PERFORMANCE_TRANSACTION_HISTORY,
} from './resolver';
import {useEffect, useState} from 'react';
import {Form} from 'antd';
import {client} from 'index';
import moment from 'moment';

export const CustomerPerformanceHook = ({adminRole}) => {
  const [form] = Form.useForm();
  const [exportForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [activeTab, setActiveTab] = useState(
    adminRole !== 'SUPERADMIN' ? 'ADMIN' : 'TEAM',
  );

  // After refresh, `adminRole` is often undefined on the first paint until Redux
  // rehydrates, so useState wrongly picks ADMIN for SUPERADMIN. Correct once role is known.
  useEffect(() => {
    if (adminRole !== 'SUPERADMIN') return;
    setActiveTab(prev => (prev === 'ADMIN' ? 'TEAM' : prev));
  }, [adminRole]);

  const [openExportModal, setOpenExportModal] = useState({
    open: false,
    email: null,
  });
  const [agent, setAgent] = useState(null);

  const STATISTICS_RESOLVER =
    activeTab === 'TEAM'
      ? GET_TEAM_AGENT_PERFORMANCE_STATISTICS
      : GET_ADMIN_AGENT_PERFORMANCE_STATISTICS;

  const HISTORY_RESOLVER =
    activeTab === 'TEAM'
      ? GET_TEAM_AGENT_PERFORMANCE_TRANSACTION_HISTORY
      : GET_ADMIN_AGENT_PERFORMANCE_TRANSACTION_HISTORY;

  const {
    data,
    refetch: refetchStatistics,
    loading,
  } = useQuery(STATISTICS_RESOLVER, {
    variables: {
      filter: {},
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: historyData,
    loading: historyLoading,
    refetch,
  } = useQuery(HISTORY_RESOLVER, {
    variables: {
      filter: {
        page: 1,
        limit: 10,
        paginate: true,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const handleExport = () => {
    client
      .query({
        query: HISTORY_RESOLVER,
        variables: {
          filter: {
            emails: [exportForm.getFieldsValue().email],
            export: true,
            createdBy: agent ? agent?.split(';')[2] : null,
            startDate: form.getFieldsValue().startDate
              ? moment(form.getFieldsValue().startDate).format()
              : null,
            endDate: form.getFieldsValue().endDate
              ? moment(form.getFieldsValue().endDate).format()
              : null,
          },
        },
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        onError: error => toast.error(error.message),
      })
      .then(({data}) => {
        const resolvedData =
          data?.[
            activeTab === 'TEAM'
              ? 'getOwnInteractionSummary'
              : 'getTeamInteractionSummary'
          ];
        if (
          resolvedData?.statusCode === 400 ||
          resolvedData?.__typename === 'Error'
        ) {
          toast.error(resolvedData.message);
        } else {
          toast.success('Data exported successfully');
          setOpenExportModal({open: false, email: null});
          exportForm.resetFields();
        }
      })
      .catch(error => toast.error(error.message));
  };

  const handleDateFilter = values => {
    setCurrentPage(1);
    const startDate = values.startDate
      ? moment(values.startDate).format()
      : null;
    const endDate = values.endDate ? moment(values.endDate).format() : null;
    refetch({
      filter: {
        page: 1,
        limit: 10,
        startDate,
        endDate,
        createdBy: agent ? agent?.split(';')[2] : null,
        paginate: true,
      },
    });
    refetchStatistics({
      filter: {
        createdBy: agent ? agent?.split(';')[2] : null,
        startDate,
        endDate,
      },
    });
  };

  useEffect(() => {
    setLimit(10);
    setCurrentPage(1);
  }, [activeTab]);

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      filter: {
        page,
        limit: pageSize,
        createdBy: agent ? agent?.split(';')[2] : null,
        paginate: true,
        startDate: form.getFieldsValue().startDate
          ? moment(form.getFieldsValue().startDate).format()
          : null,
        endDate: form.getFieldsValue().endDate
          ? moment(form.getFieldsValue().endDate).format()
          : null,
      },
    });
  };

  return {
    data:
      activeTab === 'TEAM'
        ? historyData?.getTeamAgentPerformanceTransactionHistory?.data
        : historyData?.getAdminAgentPerformanceTransactionHistory?.data,
    loading: historyLoading,
    statsLoading: loading,
    stats:
      activeTab === 'TEAM'
        ? data?.getTeamAgentPerformanceStatistics?.data
        : data?.getAdminAgentPerformanceStatistics?.data,
    activeTab,
    setActiveTab,
    form,
    openExportModal,
    setOpenExportModal,
    exportForm,
    handleDateFilter,
    handleExport,
    totalPages:
      activeTab === 'TEAM'
        ? historyData?.getTeamAgentPerformanceTransactionHistory?.pagination
            ?.total
        : historyData?.getAdminAgentPerformanceTransactionHistory?.pagination
            ?.total,
    currentPage,
    handlePageChange,
    limit,
    agent,
    setAgent,
  };
};
